import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Optin() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com';
    const scriptId = 'jsonld-optin';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Accéder à la Vidéo | Usine à Leads',
      description: "Obtenez votre usine à leads en 30 jours. Accédez à notre vidéo exclusive qui révèle la méthode complète pour générer des leads qualifiés.",
      url: `${siteUrl}/optin`,
      inLanguage: 'fr-FR',
      publisher: {
        '@type': 'Organization',
        name: 'Usine à Leads',
        url: siteUrl,
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        name: 'Vidéo exclusive - Méthode de génération de leads',
        description: "Accès gratuit et immédiat à la méthode complète de l'Usine à Leads.",
      },
    });
    document.head.appendChild(script);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 relative">
      <title>Accéder à la Vidéo Gratuite | Usine à Leads - Méthode de Génération de Leads</title>
      <meta name="description" content="Obtenez votre usine à leads en 30 jours. Inscrivez-vous pour accéder gratuitement à notre vidéo exclusive sur la génération de leads qualifiés." />
      <meta name="keywords" content="vidéo génération leads, méthode acquisition clients, leads qualifiés gratuit, formation marketing digital" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/optin`} />
      <meta property="og:title" content="Accéder à la Vidéo Gratuite | Usine à Leads" />
      <meta property="og:description" content="Inscrivez-vous et accédez gratuitement à notre vidéo exclusive sur la génération de leads qualifiés." />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/optin`} />
      <meta property="og:type" content="website" />

      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-gray-950/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="cursor-pointer">
              <img
                src="https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/512cce5c00ccd36955d2d4e00fd25839.png"
                alt="Usine à Leads"
                className="h-10 sm:h-12 w-auto"
              />
            </Link>
            <a
              href="#optin-form"
              data-iclosed-link="https://app.iclosed.io/e/usinealead/rdv-45-min-votre-business-votre-besoin"
              data-embed-type="popup"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold text-sm hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Accéder à la vidéo
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section with Blurred Video */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        {/* Blurred Video Container */}
        <div className="absolute inset-0 z-0">
          <div className="w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-gray-950 flex items-center justify-center filter blur-lg scale-110">
            {/* Video placeholder - remplacer par la vraie vidéo plus tard */}
            <div className="w-[80%] max-w-4xl aspect-video bg-gray-700/50 rounded-2xl flex items-center justify-center relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-orange-600/10 rounded-2xl"></div>
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                <i className="ri-play-fill text-4xl text-white/60"></i>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-gray-950/60"></div>
        </div>

        {/* Content over blurred video */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <div className="w-full max-w-3xl mx-auto">
            {/* Lock icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-orange-500/20 border border-orange-500/30 mb-8 animate-pulse">
              <i className="ri-lock-line text-3xl sm:text-4xl text-orange-400"></i>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Obtenez votre usine à Leads en 30 jours
            </h1>
            <p className="text-lg sm:text-xl text-white/80 mb-8 leading-relaxed font-light max-w-2xl mx-auto">
              Cette vidéo exclusive révèle notre méthode complète pour
              transformer votre acquisition en{' '}
              <strong className="text-white">machine à rendez-vous</strong>.
              Inscrivez-vous pour y accéder gratuitement.
            </p>

            {/* Blurred video preview card */}
            <div className="relative w-full max-w-2xl mx-auto mb-10">
              <div className="aspect-video bg-gray-800/80 rounded-2xl border border-white/10 overflow-hidden relative">
                {/* Fake video thumbnail blurred */}
                <div className="absolute inset-0 filter blur-md">
                  <img
                    src="https://public.readdy.ai/ai/img_res/da511240d32fdd9e46b45f14854a52f4.jpg"
                    alt="Aperçu vidéo - Méthode de génération de leads"
                    title="Vidéo exclusive - Génération de leads qualifiés"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gray-900/40"></div>

                {/* Play button overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-orange-500/90 flex items-center justify-center shadow-2xl shadow-orange-500/30 mb-4 cursor-pointer hover:scale-110 transition-transform duration-300">
                    <i className="ri-play-fill text-3xl sm:text-4xl text-white ml-1"></i>
                  </div>
                  <span className="text-white font-semibold text-sm sm:text-base bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full">
                    <i className="ri-lock-2-line mr-2"></i>Inscrivez-vous pour débloquer
                  </span>
                </div>

                {/* Duration badge */}
                <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                  <i className="ri-time-line mr-1"></i> 12:34
                </div>
              </div>
            </div>

            {/* Arrow pointing down */}
            <a href="#optin-form" className="inline-flex items-center justify-center cursor-pointer animate-bounce">
              <i className="ri-arrow-down-line text-orange-400 text-3xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section
        id="optin-form"
        className="relative z-10 py-16 sm:py-24 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950"
      >
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <i className="ri-gift-line"></i>
              <span>Accès gratuit et immédiat</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              Débloquez la vidéo complète
            </h2>
            <p className="text-lg sm:text-xl text-white/60 leading-relaxed max-w-2xl mx-auto">
              Remplissez le formulaire ci-dessous et accédez instantanément à notre méthode de{' '}
              <strong className="text-white/80">génération de leads qualifiés</strong>
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 border border-gray-100">
            <iframe
              src="https://links.usinealead.com/widget/form/hz86hZzfgPTUk7jYReGE"
              style={{
                width: '100%',
                height: '681px',
                border: 'none',
                borderRadius: '3px',
              }}
              id="inline-hz86hZzfgPTUk7jYReGE-optin"
              data-layout="{'id':'INLINE'}"
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="neverDeactivate"
              data-deactivation-value=""
              data-form-name="Form Etape 1 FUNNEL 1"
              data-height="681"
              data-layout-iframe-id="inline-hz86hZzfgPTUk7jYReGE-optin"
              data-form-id="hz86hZzfgPTUk7jYReGE"
              title="Form Etape 1 FUNNEL 1"
            />

            {/* Trust Indicators */}
            <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-200">
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-base sm:text-lg text-emerald-600 flex-shrink-0"></i>
                  <span className="whitespace-nowrap">Données sécurisées</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-video-line text-base sm:text-lg text-orange-600 flex-shrink-0"></i>
                  <span className="whitespace-nowrap">Accès immédiat</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-money-euro-circle-line text-base sm:text-lg text-teal-600 flex-shrink-0"></i>
                  <span className="whitespace-nowrap">100% gratuit</span>
                </div>
              </div>
            </div>
          </div>

          {/* Social Proof */}
          <div className="mt-10 sm:mt-14 text-center">
            <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-6 py-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 border-2 border-gray-900 flex-shrink-0"
                  ></div>
                ))}
              </div>
              <div className="text-left">
                <div className="flex gap-0.5 mb-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xs"></i>
                  ))}
                </div>
                <p className="text-white/70 text-xs sm:text-sm">
                  <strong className="text-white">+150 entreprises</strong> ont déjà
                  accédé à cette vidéo
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="relative z-10 py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-white/40">
            <p>© {new Date().getFullYear()} Usine à Leads. Tous droits réservés.</p>
            <nav className="flex flex-wrap justify-center gap-4">
              <Link
                to="/mentions-legales"
                className="hover:text-orange-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Mentions légales
              </Link>
              <Link
                to="/politique-de-confidentialite"
                className="hover:text-orange-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Politique de confidentialité
              </Link>
              <Link
                to="/cgv"
                className="hover:text-orange-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                CGV
              </Link>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-orange-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Powered by Readdy
              </a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
